// The controlled career-stage vocabulary — the SINGLE source for the values stored in
// `user.professionalTitle`. Mobile onboarding presents these (as its "training level") and writes
// the chosen one to `professionalTitle`; ad targeting then filters on that field.
//
// Ad targeting matches `professionalTitle` by SUBSTRING (a user's stored title must CONTAIN a
// targeted entry — see AdTargetingSchema / models/adTargeting.ts), so the admin + advertiser
// targeting pickers MUST offer these exact stored values. They previously offered a divergent
// hand-maintained list ('PA Student', 'Dental Student', 'Pharmacist', a bare 'Medical Student')
// whose entries could never match a real user — this const exists so those three surfaces
// (onboarding, the advertiser picker, the admin picker) can't drift again; both pickers import
// it directly. Mirrors the TOPICS idiom in topics.ts and EXAMS in exam.ts.
export const PROFESSIONAL_TITLES = [
  'Medical Student (Pre-clinical)',
  'Medical Student (Clinical)',
  'Intern',
  'Resident',
  'Fellow',
  'Attending Physician',
  'Physician Assistant (PA)',
  'Nurse Practitioner (NP)',
  'Dentist (DDS/DMD)',
  'Healthcare Professional (Other)',
  'Researcher',
] as const;

export type ProfessionalTitle = (typeof PROFESSIONAL_TITLES)[number];

// --- Title-driven field rules ---
//
// Which profile fields a title implies. Previously copy-pasted into onboarding.tsx AND
// (tabs)/profile.tsx, where the copies had DRIFTED — PA/NP were in onboarding's specialty
// allowlist and not profile's, so a PA who set a specialty could never see or edit it again while
// it kept driving the feed and billed ad targeting.

// Prefix test, not set membership, so it survives a future 'Medical Student (Research Year)'.
export const isMedicalStudent = (professionalTitle: string): boolean =>
  professionalTitle.startsWith('Medical Student');

// `satisfies` makes a typo a compile error; the Set keeps the lookup against a plain string
// (callers pass form state), so no cast is needed. PA/NP are in deliberately — they practise in a
// specialty, and excluding them is what stranded it. Students have none yet; Dentistry is one.
const SPECIALTY_BEARING_TITLES = new Set<string>([
  'Intern',
  'Resident',
  'Fellow',
  'Attending Physician',
  'Physician Assistant (PA)',
  'Nurse Practitioner (NP)',
  'Healthcare Professional (Other)',
  'Researcher',
] satisfies readonly ProfessionalTitle[]);

export const shouldShowMedicalSpecialty = (professionalTitle: string): boolean =>
  SPECIALTY_BEARING_TITLES.has(professionalTitle);

// `isStudent` is NOT a verification flag, despite what this comment used to claim —
// `studentEmailVerified` is that. In practice `isStudent` is a mirror of
// isMedicalStudent(professionalTitle): the onboarding screen derives it from the title, and the
// backend re-derives it on every PUT that carries one. The default below exists because the
// profile screen passes nothing.
export const shouldShowNpi = (professionalTitle: string, isStudent = false): boolean =>
  !isStudent && !isMedicalStudent(professionalTitle);
